import { IntegrationData } from "../types"

export const hubspotData: IntegrationData = {
  platform: {
    slug: "hubspot",
    name: "HubSpot",
    description:
      "Turn contacts into event promoters with Premagic + HubSpot integration",
    shortDescription:
      "CRM integration for marketing automation and attendee advocacy",
    logoUrl: "https://www.vectorlogo.zone/logos/hubspot/hubspot-icon.svg",
    websiteUrl: "https://www.hubspot.com/",
    category: "crm",
    tags: ["CRM", "Marketing", "Bi-Directional Sync"],
    isComingSoon: false,
  },
  heroTitle: "Turn Your Contacts Into Event Promoters",
  heroDescription:
    "Transform your HubSpot contact list into a powerful event promotion engine. Automatically generate personalized posters, launch campaigns, and drive referrals—all without leaving your CRM.",
  challengeTitle: "The Challenge",
  challengeDescription:
    "You've built a powerful contact list in HubSpot. You're running events to engage your audience. But there's a gap: how do you turn those contacts into active promoters who drive registrations and referrals?",
  challengePoints: [
    {
      title: "Email Blasts Get Ignored",
      description:
        "Traditional email campaigns have low engagement rates and don't inspire action.",
    },
    {
      title: "Social Ads Are Expensive",
      description:
        "Paid advertising increases costs without leveraging your existing warm audience.",
    },
    {
      title: "Manual Poster Creation Doesn't Scale",
      description:
        "Creating personalized content for each contact is time-consuming and impractical.",
    },
  ],
  capabilities: [
    {
      title: "Poster Widget Embedding",
      description:
        "Add Premagic's poster creation widget directly to your event registration flow",
      features: [
        "Seamless embedding with no coding required",
        "Matches your event branding",
        "Works on desktop and mobile",
        "Captures registrants at peak excitement",
      ],
    },
    {
      title: "Auto-Generate Personalized Posters",
      description:
        "Automatically create pre-filled posters for every registrant using synced HubSpot data",
      features: [
        "Contact's full name and profile photo",
        "Custom properties from your CRM",
        "Event details and branding",
        "Professional, share-ready instantly",
      ],
    },
    {
      title: "Launch Campaigns to Drive Sharing",
      description:
        "Proactively send poster campaigns to nudge contacts into action",
      features: [
        "Email campaigns with personalized posters",
        "Reminder sequences leading up to events",
        "One-click social sharing CTAs",
        "Track which contacts drive most referrals",
      ],
    },
    {
      title: "Seamless HubSpot List Sync",
      description:
        "Connect any HubSpot list to Premagic and automatically import all contact data",
      features: [
        "Manual, dynamic, or snapshot lists",
        "Full name and contact information",
        "Email, phone, and profile photos",
        "All custom properties synchronized",
      ],
    },
    {
      title: "Bi-Directional Data Flow",
      description:
        "Premagic syncs valuable data back to your HubSpot contact records",
      features: [
        "Personal photo gallery URLs",
        "Registration tracking IDs",
        "Face recognition data",
        "Engagement metrics",
      ],
    },
    {
      title: "Profile Photo Import for AI",
      description:
        "Contact avatars from HubSpot enable AI-powered poster personalization",
      features: [
        "Automatic facial recognition setup",
        "Post-event photo tagging",
        "No separate photo uploads needed",
        "Instant AI personalization",
      ],
    },
    {
      title: "AI-Powered Photo Distribution",
      description:
        "After the event, automatically tag and distribute photos to contacts",
      features: [
        "Instant facial recognition",
        "Automatic photo tagging",
        "Personal photo galleries",
        "Drive post-event engagement",
      ],
    },
  ],
  flywheelTitle: "The Referral Flywheel",
  flywheelDescription:
    "Here's how the integration works in practice to turn your HubSpot list into a registration growth engine:",
  flywheelSteps: [
    {
      number: 1,
      title: "List",
      description: "Build your event list in HubSpot",
    },
    {
      number: 2,
      title: "Sync",
      description: "Connect the list to Premagic",
    },
    {
      number: 3,
      title: "Personalize",
      description: "Auto-generate posters for each contact",
    },
    {
      number: 4,
      title: "Campaign",
      description: "Send poster campaigns through HubSpot workflows",
    },
    {
      number: 5,
      title: "Share",
      description: "Contacts share personalized posters to their networks",
    },
    {
      number: 6,
      title: "Register",
      description: "New contacts sign up through shared links",
    },
    {
      number: 7,
      title: "Repeat",
      description: "Every new registration multiplies your reach",
    },
  ],
  howItWorksSteps: [
    {
      number: 1,
      title: "Connect",
      description: "Link your HubSpot account to Premagic via access token",
    },
    {
      number: 2,
      title: "Select",
      description:
        "Choose which HubSpot list to connect to your Premagic project",
    },
    {
      number: 3,
      title: "Configure",
      description: "Select which contact fields to sync",
    },
    {
      number: 4,
      title: "Embed",
      description: "Add the poster widget to your registration pages",
    },
    {
      number: 5,
      title: "Campaign",
      description: "Send personalized poster campaigns to drive sharing",
    },
    {
      number: 6,
      title: "Track",
      description:
        "Monitor referrals and registration growth in both platforms",
    },
    {
      number: 7,
      title: "Deliver",
      description:
        "After the event, distribute photos using the same contact data",
    },
  ],
  capabilitiesTable: [
    {
      feature: "Poster Widget Embedding",
      description: "Add shareable poster creation to your registration pages",
    },
    {
      feature: "Pre-filled Posters",
      description:
        "Auto-generate personalized posters from synced contact data",
    },
    {
      feature: "Campaign Distribution",
      description: "Send poster campaigns to drive sharing and referrals",
    },
    {
      feature: "HubSpot List Sync",
      description:
        "Connect manual, dynamic, or snapshot lists to Premagic projects",
    },
    {
      feature: "Contact Import",
      description:
        "Full contact sync including names, emails, phone, and avatars",
    },
    {
      feature: "Profile Photo Import",
      description:
        "Use HubSpot avatars for poster personalization and facial recognition",
    },
    {
      feature: "Custom Property Sync",
      description: "Import all user-created HubSpot properties",
    },
    {
      feature: "Bi-directional Sync",
      description:
        "Push gallery URLs, registration IDs, and face IDs back to HubSpot",
    },
    {
      feature: "Auto Property Creation",
      description:
        "Premagic automatically creates custom properties in HubSpot",
    },
    {
      feature: "AI Photo Distribution",
      description: "Post-event photo delivery powered by synced contact data",
    },
  ],
  targetAudience: [
    {
      title: "Marketing Teams",
      description:
        "Using HubSpot who want to amplify event reach organically through their existing contact base",
    },
    {
      title: "Event Marketers",
      description:
        "Looking to leverage existing CRM data for event promotion and attendee engagement",
    },
    {
      title: "Growth Teams",
      description:
        "Who need to hit registration targets without increasing ad spend through word-of-mouth",
    },
    {
      title: "Agencies",
      description:
        "Managing client events and HubSpot accounts together for integrated campaigns",
    },
  ],
  ctaTitle: "Ready to Turn Your HubSpot Contacts Into Event Promoters?",
  ctaDescription:
    "Enable the HubSpot integration for your account and start driving referrals today.",
  seo: {
    title:
      "Premagic + HubSpot Integration | Turn Contacts Into Event Promoters",
    description:
      "Transform your HubSpot contact list into an event promotion engine. Auto-generate personalized posters, launch campaigns, and drive referrals—all without leaving your CRM.",
    keywords: [
      "HubSpot integration",
      "event marketing",
      "CRM integration",
      "poster generation",
      "event promotion",
      "contact management",
      "marketing automation",
      "referral marketing",
    ],
  },
}
